import { CommonModule } from '@angular/common';
import {
    ChangeDetectionStrategy,
    Component,
    DestroyRef,
    effect,
    inject,
    input,
    output,
    signal,
    untracked,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { HdmuiInfoBoxModule } from '@heidelberg/hdmui-angular';
import { TranslateModule } from '@ngx-translate/core';
import {
    EMPTY,
    Observable,
    catchError,
    defaultIfEmpty,
    forkJoin,
    map,
    of,
    scan,
    switchMap,
    takeWhile,
    tap,
    timer,
} from 'rxjs';

import { ActivitiesApiService } from '@data-terminal/data-access/activities-api/activities-api.service';
import { TimeModeEntryCardContentComponent } from '@data-terminal/feature-workstation-details/components/time-mode-entry-card-content/time-mode-entry-card-content.component';
import { PureTimeApiService } from '@data-terminal/feature-workstation-details/services/pure-time-api/pure-time-api.service';
import { TimeTransferState, TimeTransferStatus } from '@data-terminal/shared-models/dialogs/sign-off-dialog';
import { Machine, SignOffMachine } from '@data-terminal/shared-models/machine';
import { Activity } from '@data-terminal/shared-models/operation';
import { TimeModeEntry, TimeModeStatus } from '@data-terminal/shared-models/time-mode-entry.interface';

// Poll the backend every 5s for up to 60 attempts (~5 minutes) before declaring the transfer failed.
const POLLING_INTERVAL_MS = 5000;
const MAX_POLL_ATTEMPTS = 60;

interface TimeEntryGroup {
    shiftStart: number;
    deviceId: string;
}

interface PollResult {
    remaining: TimeModeEntry[];
    attempt: number;
}

export interface EntryTransferRow {
    entry: TimeModeEntry;
    transferred: boolean;
    hasError: boolean;
}

type EntryTransferState = Pick<EntryTransferRow, 'transferred' | 'hasError'>;

export interface MachineTransferCard {
    machine: Machine;
    deviceId: string;
    total: number;
    remaining: number;
    status: TimeTransferStatus;
    entries: EntryTransferRow[];
}

@Component({
    selector: 'data-terminal-sign-off-time-transfer',
    templateUrl: './sign-off-time-transfer.component.html',
    styleUrls: ['./sign-off-time-transfer.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        CommonModule,
        TranslateModule,
        MatButtonModule,
        MatCardModule,
        MatIconModule,
        MatProgressBarModule,
        HdmuiInfoBoxModule,
        TimeModeEntryCardContentComponent,
    ],
})
export class SignOffTimeTransferComponent {
    public readonly unsubmittedTimeEntries = input<TimeModeEntry[]>([]);
    public readonly signOffMachines = input<SignOffMachine[]>([]);
    public readonly stateChange = output<TimeTransferState>();

    public readonly TimeTransferStatus = TimeTransferStatus;
    public readonly status = signal<TimeTransferStatus>(TimeTransferStatus.IDLE);
    public readonly processed = signal<number>(0);
    public readonly total = signal<number>(0);
    public readonly machineCards = signal<MachineTransferCard[]>([]);
    public readonly activities = signal<Activity[]>([]);

    readonly #pureTimeApiService = inject(PureTimeApiService);
    readonly #activitiesApiService = inject(ActivitiesApiService);
    readonly #destroyRef = inject(DestroyRef);
    readonly #fetchedDeviceIds = new Set<string>();
    readonly #entryTransferStateById = new Map<string | undefined, EntryTransferState>();

    constructor() {
        effect(() => {
            this.unsubmittedTimeEntries();
            this.signOffMachines();
            untracked(() => this.#onInputsChange());
        });
    }

    public start(): void {
        if (this.status() === TimeTransferStatus.TRANSFERRING) {
            return;
        }

        const trackedEntries = this.#getTrackedEntries();

        if (this.#hasErroredEntries(trackedEntries)) {
            const cards = this.#buildMachineCards();
            this.machineCards.set(cards);
            this.total.set(trackedEntries.length);
            this.processed.set(this.#countTransferred(cards));
            this.#updateState(TimeTransferStatus.ERROR);
            return;
        }

        const entriesToSubmit = trackedEntries.filter(
            (entry) => !(this.#entryTransferStateById.get(entry.id)?.transferred ?? false)
        );
        entriesToSubmit.forEach((entry) => this.#persistEntryState(entry.id, false, false));

        this.total.set(trackedEntries.length);
        this.processed.set(trackedEntries.length - entriesToSubmit.length);
        this.machineCards.set(this.#buildMachineCards());

        if (!entriesToSubmit.length) {
            this.#updateState(TimeTransferStatus.DONE);
            return;
        }

        this.#updateState(TimeTransferStatus.TRANSFERRING);
        this.#submitEntries(entriesToSubmit);
    }

    #submitEntries(entriesToSubmit: TimeModeEntry[]): void {
        const trackedIds = new Set(entriesToSubmit.map((entry) => entry.id));
        const dispatches: Observable<boolean>[] = this.#groupEntries(entriesToSubmit).map((group) =>
            this.#pureTimeApiService.startSubmittingEntries(group.shiftStart, group.deviceId).pipe(
                map(() => true),
                defaultIfEmpty(false)
            )
        );

        forkJoin(dispatches)
            .pipe(
                switchMap((dispatchResults) => {
                    // All-or-nothing: if any machine's dispatch fails, interrupt the whole transfer
                    // so the operator leaves the dialog and retries on the device.
                    if (dispatchResults.some((dispatched) => !dispatched)) {
                        this.#markCardsAsError();
                        this.#updateState(TimeTransferStatus.ERROR, true);
                        return EMPTY;
                    }
                    return this.#pollUntilResolved(trackedIds);
                }),
                takeUntilDestroyed(this.#destroyRef)
            )
            .subscribe();
    }

    #pollUntilResolved(trackedIds: Set<string | undefined>): Observable<PollResult> {
        // Start listening immediately (no leading delay) so the progress reflects the transfer from
        // the first poll. Entries stay PENDING until the device confirms submission, so the step is
        // only marked DONE once every tracked entry has actually left the unsubmitted list.
        return timer(0, POLLING_INTERVAL_MS).pipe(
            switchMap(() => this.#pureTimeApiService.getUnsubmittedTimeEntries()),
            map((entries) => entries.filter((entry) => trackedIds.has(entry.id))),
            scan<TimeModeEntry[], PollResult>((acc, remaining) => ({ remaining, attempt: acc.attempt + 1 }), {
                remaining: [],
                attempt: 0,
            }),
            tap(({ remaining, attempt }) => {
                this.processed.set(this.total() - remaining.length);
                this.#updateCards(remaining);
                const resolved = this.#resolveTransferStatus(remaining, attempt);
                this.#updateState(resolved, resolved === TimeTransferStatus.ERROR);
            }),
            takeWhile(() => this.status() === TimeTransferStatus.TRANSFERRING, true)
        );
    }

    #onInputsChange(): void {
        this.#loadActivities();
        if (this.status() === TimeTransferStatus.TRANSFERRING) {
            return;
        }

        const cards = this.#buildMachineCards();
        this.machineCards.set(cards);
        this.total.set(cards.reduce((sum, card) => sum + card.total, 0));
        this.processed.set(this.#countTransferred(cards));
        // Only react to a pre-existing error here so the next button stays blocked.
        // DONE must never be emitted from an input change: it would make the parent dialog
        // auto-advance and mark entries as submitted on open, without the user starting a transfer.
        this.#updateState(
            cards.some((card) => card.status === TimeTransferStatus.ERROR)
                ? TimeTransferStatus.ERROR
                : TimeTransferStatus.IDLE
        );
    }

    #getTrackedEntries(): TimeModeEntry[] {
        const selectedDeviceIds = new Set(this.signOffMachines().map((signOffMachine) => signOffMachine.machineId));
        return this.unsubmittedTimeEntries().filter(
            (entry) => !!entry?.id && !!entry?.deviceId && selectedDeviceIds.has(entry.deviceId)
        );
    }

    #resolveTransferStatus(remaining: TimeModeEntry[], attempt: number): TimeTransferStatus {
        if (remaining.some((entry) => entry.status === TimeModeStatus.ERROR)) {
            return TimeTransferStatus.ERROR;
        }
        if (remaining.length === 0) {
            return TimeTransferStatus.DONE;
        }
        if (attempt >= MAX_POLL_ATTEMPTS) {
            return TimeTransferStatus.ERROR;
        }
        return TimeTransferStatus.TRANSFERRING;
    }

    #loadActivities(): void {
        const deviceIds = Array.from(new Set(this.#getTrackedEntries().map((entry) => entry.deviceId)));
        const devicesToFetch = deviceIds.filter((deviceId) => !this.#fetchedDeviceIds.has(deviceId));
        if (!devicesToFetch.length) {
            return;
        }
        // Activities are supplementary enrichment for the entry cards, so a failed request degrades
        // silently to an empty list instead of surfacing an error and blocking the transfer.
        // Mark a device as fetched only after its request succeeds, and isolate failures per device
        // so one failing request neither aborts the others nor blocks a later retry.
        const activityRequests: Observable<Activity[]>[] = devicesToFetch.map((deviceId) =>
            this.#activitiesApiService.getActivities(deviceId).pipe(
                tap(() => this.#fetchedDeviceIds.add(deviceId)),
                catchError(() => of<Activity[]>([]))
            )
        );
        forkJoin(activityRequests)
            .pipe(takeUntilDestroyed(this.#destroyRef))
            .subscribe((results) => {
                this.activities.update((current) => [...current, ...results.flat()]);
            });
    }

    #buildMachineCards(): MachineTransferCard[] {
        const cardsByDevice = new Map<string, MachineTransferCard>();
        this.#getTrackedEntries().forEach((entry) => {
            let card = cardsByDevice.get(entry.deviceId);
            if (!card) {
                const machine = this.signOffMachines().find(
                    (signOffMachine) => signOffMachine.machineId === entry.deviceId
                )?.machine;
                if (!machine) {
                    return;
                }
                card = {
                    machine,
                    deviceId: entry.deviceId,
                    total: 0,
                    remaining: 0,
                    status: TimeTransferStatus.IDLE,
                    entries: [],
                };
                cardsByDevice.set(entry.deviceId, card);
            }
            const state = this.#resolveEntryState(entry);
            card.total += 1;
            card.entries.push({ entry, transferred: state.transferred, hasError: state.hasError });
        });
        cardsByDevice.forEach((card) => {
            const deviceRemaining = card.entries.filter((row) => !row.transferred);
            card.remaining = deviceRemaining.length;
            card.status = this.#resolveCardStatus(card.entries, deviceRemaining.length, false);
        });
        return Array.from(cardsByDevice.values());
    }

    #countTransferred(cards: MachineTransferCard[]): number {
        return cards.reduce((sum, card) => sum + card.entries.filter((row) => row.transferred).length, 0);
    }

    #resolveEntryState(entry: TimeModeEntry): EntryTransferState {
        const persisted = this.#entryTransferStateById.get(entry.id);
        const transferred = entry.status === TimeModeStatus.SUBMITTED || (persisted?.transferred ?? false);
        const hasError = !transferred && (entry.status === TimeModeStatus.ERROR || (persisted?.hasError ?? false));
        return { transferred, hasError };
    }

    #hasErroredEntries(entries: TimeModeEntry[]): boolean {
        return entries.some((entry) => this.#resolveEntryState(entry).hasError);
    }

    #resolveCardStatus(entries: EntryTransferRow[], remaining: number, active: boolean): TimeTransferStatus {
        if (entries.some((row) => row.hasError)) {
            return TimeTransferStatus.ERROR;
        }
        if (entries.length && remaining === 0) {
            return TimeTransferStatus.DONE;
        }
        return active ? TimeTransferStatus.TRANSFERRING : TimeTransferStatus.IDLE;
    }

    #updateCards(remaining: TimeModeEntry[]): void {
        const remainingById = new Map<string | undefined, TimeModeEntry>(remaining.map((entry) => [entry.id, entry]));

        this.machineCards.update((cards) =>
            cards.map((card) => {
                const entries = card.entries.map((row) => {
                    const stillRemaining = remainingById.get(row.entry.id);
                    const transferred = !stillRemaining;
                    const hasError = stillRemaining?.status === TimeModeStatus.ERROR;
                    this.#persistEntryState(row.entry.id, transferred, hasError);
                    return { entry: row.entry, transferred, hasError };
                });
                const remainingCount = entries.filter((row) => !row.transferred).length;
                return {
                    ...card,
                    remaining: remainingCount,
                    status: this.#resolveCardStatus(entries, remainingCount, true),
                    entries,
                };
            })
        );
    }

    #markCardsAsError(): void {
        this.machineCards.update((cards) =>
            cards.map((card) => ({
                ...card,
                status: TimeTransferStatus.ERROR,
                entries: card.entries.map((row) => {
                    this.#persistEntryState(row.entry.id, row.transferred, true);
                    return { ...row, hasError: true };
                }),
            }))
        );
    }

    #persistEntryState(id: string | undefined, transferred: boolean, hasError: boolean): void {
        this.#entryTransferStateById.set(id, { transferred, hasError });
    }

    #groupEntries(entries: TimeModeEntry[]): TimeEntryGroup[] {
        const groups = new Map<string, TimeEntryGroup>();
        entries.forEach((entry) => {
            const shiftStart = Number(entry.shiftStart);
            if (!Number.isFinite(shiftStart)) {
                return;
            }
            const key = `${entry.deviceId}_${entry.shiftStart}`;
            if (!groups.has(key)) {
                groups.set(key, { shiftStart, deviceId: entry.deviceId });
            }
        });
        return Array.from(groups.values());
    }

    #updateState(status: TimeTransferStatus, transferFailed = false): void {
        this.status.set(status);
        this.stateChange.emit({ status, processed: this.processed(), total: this.total(), transferFailed });
    }
}
